import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import type { Lt2CardTargets } from '@/stores/lt2FocusStore';

/**
 * D-020 V2B — register a derivative card as a hotkey-focus target.
 *
 * The resolver ref is refreshed EVERY render, so `resolveFocused()` always
 * reads exactly what the card is currently displaying. Registration keys on
 * (id, enabled): a changed id (period/tab re-key) unregisters the old card —
 * clearing the focus if it was held — before registering the new one.
 *
 * Returns whether this card currently holds the focus (drives the ring).
 */
export function useLt2Card(
  id: string | null,
  enabled: boolean,
  resolver: () => Lt2CardTargets | null,
): boolean {
  const resolverRef = useRef(resolver);

  // Refresh before the browser can paint the new card values. This keeps the
  // registered callback stable without reading or mutating a ref during
  // render, while preserving the invariant that a visible hotkey target and
  // its resolver describe the same render.
  useLayoutEffect(() => {
    resolverRef.current = resolver;
    // The header target readout derives its label/Ready/Paused text from
    // resolveFocused(); re-registering the ref is invisible to subscribers,
    // so the FOCUSED card announces its re-render through the store's
    // presentation seq. Only the focused card bumps — the readout re-derives
    // exactly when its subject may have changed, and rows never subscribe to
    // the seq, so no render loop is possible.
    const store = useLt2FocusStore.getState();
    if (enabled && id != null && store.focusedCardId === id) store.notePresentation();
  });

  useEffect(() => {
    if (!enabled || id == null) return;
    return useLt2FocusStore.getState().register(id, () => resolverRef.current());
  }, [id, enabled]);

  return useLt2FocusStore((s) => enabled && id != null && s.focusedCardId === id);
}
